'use client'

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface Logger {
  info: (message: string, args: any[], userId?: string) => void;
  warn: (message: string, args: any[], userId?: string) => void;
  error: (error: Error, message: string, args: any[], userId?: string) => void;
  debug: (message: string, args: any[], userId?: string) => void;
}

let sendToNewRelic: (level: LogLevel, error: Error | null | undefined, message: string, args: any[], userId?: string) => void = () => {};

// Only initialize New Relic in browser context
if (typeof window !== 'undefined') {
  const { BrowserAgent } = require('@newrelic/browser-agent/loaders/browser-agent');
  const { Logging } = require('@newrelic/browser-agent/features/logging');
  const { SessionTrace } = require('@newrelic/browser-agent/features/session_trace');
  const { GenericEvents } = require('@newrelic/browser-agent/features/generic_events');
  const { JSErrors } = require('@newrelic/browser-agent/features/jserrors');

  interface NewRelicConfig {
    init: {
      distributed_tracing: { enabled: boolean, allowed_origins: string[] };
      privacy: { cookies_enabled: boolean };
    };
    info: {
      licenseKey: string;
      applicationID: string;
      sa: number;
    };
    loader_config: {
      xpid: string;
    };
    features: [
      typeof Logging,
      typeof SessionTrace,
      typeof GenericEvents,
      typeof JSErrors
    ]
  }

  let browserAgent: typeof BrowserAgent | null = null;
  
  const config: NewRelicConfig = {
    init: {
      distributed_tracing: { enabled: true, allowed_origins: ['https://bam.nr-data.net'] },
      privacy: { cookies_enabled: true },
    },
    info: {
      licenseKey: process.env.NEXT_PUBLIC_NEW_RELIC_LICENSE_KEY || '',
      applicationID: process.env.NEXT_PUBLIC_NEW_RELIC_APP_ID || '',
      sa: 1,
    },
    loader_config: {
      xpid: process.env.NEXT_PUBLIC_NEW_RELIC_APP_ID || '', // Use app ID as xpid
    },
    features: [
      Logging,
      SessionTrace,
      GenericEvents,
      JSErrors
    ]
  };

  try {
    browserAgent = new BrowserAgent(config);
  } catch (error) {
    console.warn('Failed to initialize New Relic Browser Agent:', error);
  }

  sendToNewRelic = (level: LogLevel, error: Error | null | undefined, message: string, args: any[], userId?: string) => {
    console.log( level )
    console.log( message )
    console.log( args )
    console.log( args.length )
    console.log( userId )

    if (!browserAgent) {
      return;
    }

    const logData = {
      level,
      timestamp: new Date().toISOString(),
      ...(userId && { userId }),
      ...(args.length > 0 && { additionalInfo: JSON.stringify(args) })
    };

    if (level === 'error' && error) {
      browserAgent.noticeError(error, { ...logData, message });
    } else {
      try {
        browserAgent.addPageAction('Log', {
          ...logData,
          message: message.substring(0, 1000) // Limit message length
          // additionalInfo: logData.additionalInfo ? 
          //   logData.additionalInfo.substring(0, 1000) : undefined // Limit additionalInfo length if it exists
        });

        console.log( 'logData', logData )
      } catch (error) {
        console.warn('Failed to send log to New Relic:', error);
      }
    }
  };
}

const logger: Logger = {
  info: (message: string, args: any[], userId?: string) => {
    console.log(`[INFO] ${message}`, args);
    sendToNewRelic('info', null, message, args, userId);
  },
  warn: (message: string, args: any[], userId?: string) => {
    console.warn(`[WARN] ${message}`, args);
    sendToNewRelic('warn', null, message, args, userId);
  },
  error: (error: Error, message: string, args: any[], userId?: string) => {
    console.error(`[ERROR] ${message}`, args);
    sendToNewRelic('error', error, message, args, userId);
  },
  debug: (message: string, args: any[], userId?: string) => {
    console.debug(`[DEBUG] ${message}`, args);
    sendToNewRelic('debug', null, message, args, userId);
  },
};

export default logger;